import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { formatMomentDate } from '../../../../../utils/dateHelper';
import { fetchUserMarketplaceListings } from '../../../../../redux/slice/allUsersSlice';
import CustomTable from '../../../../../components/table/CustomTable';
import StatusChip from '../../../../../components/statusChip/StatusChip';
import { PlusIcon } from '../../../../../assets/icons';
import { routes } from '../../../../../routes/pageRoutes';
import DeletePopup from '../../../../../components/delete-popup';
import MarketplaceUserListingModal from '../../../../MarketPlace/MarketplaceUserListingModal';
import { deleteUserListing } from '../../../../../redux/slice/marketplaceSlice';
import PreviewModal from '../../../../../components/modals/previewModal';
import { IconBtn } from '../../../../../components/commonButton/btns';

const ImageSwiper = ({ media, title, onImageClick }) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    if (!media || media.length === 0) {
        return (
            <div
                className="bg-light rounded border d-flex align-items-center justify-content-center text-muted small"
                style={{ width: "50px", height: "50px" }}
            >
                N/A
            </div>
        );
    }

    const handlePrev = (e) => {
        e.stopPropagation();
        setCurrentIndex((prev) => (prev === 0 ? media.length - 1 : prev - 1));
    };

    const handleNext = (e) => {
        e.stopPropagation();
        setCurrentIndex((prev) => (prev === media.length - 1 ? 0 : prev + 1));
    };

    const currentMedia = media[currentIndex];

    return (
        <div className="position-relative rounded-3 overflow-hidden border shadow-sm" style={{ width: "50px", height: "50px", flexShrink: 0, backgroundColor: '#f8f9fa' }}>
            {currentMedia?.mimetype === "video/mp4" ?
                <video src={currentMedia.file?.url || currentMedia.url}
                    alt={title || "Product Media"}
                    className="w-100 h-100"
                    style={{ objectFit: "cover" }} /> :
                <img
                    src={currentMedia.url || currentMedia.file?.url}
                    alt={title || "Listing Media"}
                    className="w-100 h-100"
                    style={{ objectFit: "cover", cursor: "pointer" }}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (onImageClick) onImageClick(currentIndex);
                    }}
                    onError={(e) => { e.target.src = '/images/image-placeholder.png'; }}
                />}

            {media.length > 1 && (
                <>
                    <button
                        onClick={handlePrev}
                        className="position-absolute top-50 start-0 translate-middle-y border-0 bg-dark bg-opacity-50 text-white d-flex align-items-center justify-content-center"
                        style={{ width: "15px", height: "15px", borderRadius: "0 2px 2px 0", cursor: "pointer", fontSize: "10px", padding: 0 }}
                    >
                        &#10094;
                    </button>
                    <button
                        onClick={handleNext}
                        className="position-absolute top-50 end-0 translate-middle-y border-0 bg-dark bg-opacity-50 text-white d-flex align-items-center justify-content-center"
                        style={{ width: "15px", height: "15px", borderRadius: "2px 0 0 2px", cursor: "pointer", fontSize: "10px", padding: 0 }}
                    >
                        &#10095;
                    </button>
                    <div className="position-absolute bottom-0 start-50 translate-middle-x d-flex gap-1 mb-1">
                        {media.map((_, idx) => (
                            <div
                                key={idx}
                                className="rounded-circle"
                                style={{
                                    width: "3px",
                                    height: "3px",
                                    backgroundColor: currentIndex === idx ? "white" : "rgba(255,255,255,0.5)"
                                }}
                            />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

const getStatusBg = (status) => {
    const lowerStatus = status?.toLowerCase();
    switch (lowerStatus) {
        case "pending":
            return "warning";
        case "approved":
            return "success";
        case "published":
            return "blue";
        case "rejected":
            return "danger";
        case "closed":
            return "secondary";
        case "sold":
            return "blue";
        default:
            return "";
    }
};

const MarketplaceListings = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);

    const [showModal, setShowModal] = useState(false);
    const [editData, setEditData] = useState(null);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [previewMediaArray, setPreviewMediaArray] = useState([]);
    const [previewInitialIndex, setPreviewInitialIndex] = useState(0);
    const [showDeletePopup, setShowDeletePopup] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const {
        marketplaceListings,
        marketplaceListingsTotal,
        marketplaceListingsLoading
    } = useSelector((state) => state.allUsers);

    useEffect(() => {
        if (id) {
            dispatch(fetchUserMarketplaceListings({ id, page, limit }));
        }
    }, [id, page, limit, dispatch]);

    const handleDeleteListing = () => {
        if (!itemToDelete) return;
        setDeleting(true);
        dispatch(deleteUserListing(itemToDelete))
            .unwrap()
            .then(() => {
                setShowDeletePopup(false);
                setItemToDelete(null);
                dispatch(fetchUserMarketplaceListings({ id, page, limit }));
            })
            .catch((error) => {
                console.error("Failed to delete listing:", error);
            })
            .finally(() => {
                setDeleting(false);
            });
    };
    const actionList = [
        {
            title: 'View', handler: (row) => {
                const targetRoute = row.seller_type === 'company'
                    ? routes.viewMarketplaceBulkOffer.replace(":id", row.id)
                    : routes.viewMarketplaceListing.replace(":id", row.id);
                navigate(targetRoute);
            }
        },
        {
            title: 'Edit', handler: (row) => {
                setEditData(row);
                setShowModal(true);
            }
        },
        {
            title: 'Delete', handler: (row) => {
                setItemToDelete(row.id);
                setShowDeletePopup(true);
            }, isDanger: true
        },
    ];
    const columns = [
        {
            header: 'Images',
            cell: (_, item) => (
                <ImageSwiper
                    media={item.photos}
                    title={item.name}
                    onImageClick={(index) => {
                        if (!item.photos || item.photos.length === 0) return;
                        const mediaArray = item.photos.map(photo => ({
                            url: photo.url || photo.file?.url,
                            title: item.name || "Listing Image",
                            type: "image"
                        }));
                        setPreviewMediaArray(mediaArray);
                        setPreviewInitialIndex(index);
                        setShowPreviewModal(true);
                    }}
                />
            )
        },
        {
            header: 'Name',
            cell: (_, item) => <div className="fw-medium text-truncate" style={{ maxWidth: "200px" }}>{item.name || "-"}</div>
        },
        {
            header: 'Category',
            cell: (_, item) => item.category?.name || "Uncategorized"
        },
        {
            header: 'Listing Type',
            cell: (_, item) => {
                const type = item.user_listing_details?.listing_type || "-";
                return <span className="text-capitalize">{type}</span>;
            }
        },
        {
            header: 'Pricing Details',
            cell: (_, item) => {
                const details = item.user_listing_details;
                const listingType = details?.listing_type || 'sell';
                const price = details?.price ? parseFloat(details.price) : 0;
                const mrp = details?.mrp ? parseFloat(details.mrp) : 0;
                const discount = details?.discount ? parseFloat(details.discount) : 0;
                const priceUnit = details?.price_unit || '';

                const formatLabel = (str) => {
                    if (!str) return '';
                    return str.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                };

                if (listingType === 'rent' || listingType === 'service') {
                    return (<>
                        <p className="text-secondary me-1 fs-sm text-nowrap">Price:</p>
                        <span className="fw-semibold text-success text-nowrap">
                            {price ? `₹${price.toLocaleString()} / ${formatLabel(priceUnit)}` : '-'}
                        </span>
                    </>);
                }

                return (
                    <div className="d-flex flex-column align-items-start fs-sm text-nowrap">
                        <div>
                            <span className="text-secondary me-1">MRP:</span>
                            <span className="text-muted text-decoration-line-through">{mrp ? `₹${mrp.toLocaleString()}` : '-'}</span>
                        </div>
                        <div>
                            <span className="text-secondary me-1">Price:</span>
                            <span className="fw-semibold text-success">{price ? `₹${price.toLocaleString()}` : '-'}</span>
                        </div>
                        <div>
                            <span className="text-secondary me-1">Discount:</span>
                            <span className="badge bg-success bg-opacity-10 text-success fw-semibold" style={{ fontSize: '10px' }}>
                                {discount ? `${discount}%` : '0%'}
                            </span>
                        </div>
                    </div>
                );
            }
        },
        {
            header: 'Status',
            cell: (_, item) => (
                item.status ? (
                    <StatusChip
                        bg={getStatusBg(item.status)}
                        pill
                        title={item.status}
                        className="text-capitalize"
                    />
                ) : (
                    "-"
                )
            )
        },
        {
            header: 'Created At',
            cell: (_, item) => formatMomentDate(item.created_at, "DD-MM-YYYY hh:mm A")
        },
        {
            header: 'Updated At',
            cell: (_, item) => formatMomentDate(item.updated_at, "DD-MM-YYYY hh:mm A")
        }
    ];

    const totalPages = Math.ceil(marketplaceListingsTotal / limit);

    return (
        <div >
            <div className="d-flex align-items-center justify-content-between mb-4">
                <h6 className="fw-bold mb-0" style={{ fontSize: '18px' }}>Marketplace Listings</h6>
                <IconBtn size={32} icon={<PlusIcon size={16} />} className='rounded-circle' onClick={() => {
                    setEditData(null);
                    setShowModal(true);
                }} />
            </div>

            <div className="border rounded-3 overflow-hidden">
                <CustomTable
                    columns={columns}
                    data={marketplaceListings || []}
                    loading={marketplaceListingsLoading}
                    actionList={actionList}
                    emptyMessage="No marketplace listings found."
                    pagination={{
                        currentPage: page,
                        totalPages,
                        total: marketplaceListingsTotal,
                        onPageChange: (newPage) => setPage(newPage),
                        pageSize: limit,
                        setPageSize: (newLimit) => {
                            setLimit(newLimit);
                            setPage(1);
                        },
                        setCurrentPage: (newPage) => setPage(newPage)
                    }}
                />
            </div>

            <MarketplaceUserListingModal
                isOpen={showModal}
                onClose={() => {
                    setShowModal(false);
                    setEditData(null);
                }}
                editData={editData}
                userId={id}
                onSuccess={() => {
                    dispatch(fetchUserMarketplaceListings({ id, page, limit }));
                }}
                title={editData ? "Edit User Listing" : "Add User Listing"}
                size="lg"
                className="common-modal"
            />

            <DeletePopup
                show={showDeletePopup}
                onHide={() => {
                    setShowDeletePopup(false);
                    setItemToDelete(null);
                }}
                onConfirm={handleDeleteListing}
                isLoading={deleting}
                saveTitle="Delete"
            />

            <PreviewModal
                show={showPreviewModal}
                onHide={() => setShowPreviewModal(false)}
                previewMedia={previewMediaArray}
                initialIndex={previewInitialIndex}
            />
        </div>
    );
};

export default MarketplaceListings;
